import { FC, memo } from 'react';

import Link from 'next/link';

import { DateTime } from 'luxon';

import { SaleCaseItem } from '@type-def_iqfu/public/SaleCaseItem';

import { MonthOutputFormat, LandCurrentStatus } from '@constants_iqfu';

import styles from './SaleCaseCard.module.scss';

type Props = {
  data: SaleCaseItem;
  //会社詳細では会社名表示しない
  showCompanyName: boolean;
  //価格系の末端ページでは同ページリンクは表示しない
  showPlace: boolean;
  index: number;
  pageLabel: string;
};

const SaleCaseCard: FC<Props> = memo(({ data, showCompanyName = true, showPlace = true, index, pageLabel }) => {
  const getBuildingAge = (built_date: string | null): string | '不明' => {
    if (!built_date) return '不明';
    const currentDate = DateTime.local().startOf('day');
    const builtDate = DateTime.fromISO(built_date).startOf('day');
    // 経過期間を計算する
    const result = currentDate.diff(builtDate, ['years', 'months', 'days']).toObject();
    if (!result.years) return '0年';
    return `${result.years}年`;
  };

  const IconPath = {
    mansion: '/images/iqfu/public/sale_case_cassette/mansion_icon.svg',
    land: '/images/iqfu/public/sale_case_cassette/land_icon.svg',
    used_house: '/images/iqfu/public/sale_case_cassette/house_icon.svg',
    new_house: '/images/iqfu/public/sale_case_cassette/house_icon.svg',
  };

  const IconClassName = {
    land: styles.land_icon,
    mansion: styles.mansion_icon,
    used_house: styles.house_icon,
    new_house: styles.house_icon,
  };

  return (
    <div className={styles.card}>
      {data.new_label ? <div className={styles.new_label}>NEW</div> : <></>}

      <div className={styles.top_col}>
        <div className={styles.price_icon_stacks}>
          <div className={styles.icon_wrapper}>
            <img src={IconPath[data.property_type]} className={IconClassName[data.property_type]} alt="" />
          </div>
          {data.price && <p className={styles.price}>{data.price.toLocaleString()}万円</p>}
        </div>
        <p className={styles.date}>{DateTime.fromISO(data.contract_date).toFormat(MonthOutputFormat)}</p>
      </div>

      {showPlace && (
        <div className={styles.place}>
          {data.property_type !== 'mansion' ? (
            <Link href={`/landhouses/${data.town.code}`} className={styles.price_page_link}>
              {`${data.prefecture.name}${data.city.name}${data.town.name}`}
            </Link>
          ) : (
            <Link href={`/mansions/${data.mansion_id}`} className={styles.price_page_link}>
              {data.mansion_name}
            </Link>
          )}
        </div>
      )}
      <div className={`${styles.detail_data_area} ${showCompanyName ? '' : styles.mb0}`}>
        {data.property_type === 'mansion' && (
          <>
            <p className={styles.detail_data_item}>階数:{data.floor}階</p>
            <p className={styles.detail_data_item}>専有面積:{data.occupied_area}㎡</p>
          </>
        )}
        {data.property_type === 'used_house' && (
          <>
            <p className={styles.detail_data_item}>階数:{data.floor}階</p>
            <p className={styles.detail_data_item}>築年数:{getBuildingAge(data.built_date)}</p>
            <p className={styles.detail_data_item}>建物面積:{data.building_area}㎡</p>
            <p className={styles.detail_data_item}>土地面積:{data.land_area}㎡</p>
          </>
        )}
        {data.property_type === 'new_house' && (
          <>
            <p className={styles.detail_data_item}>階数:{data.floor}階</p>
            <p className={styles.detail_data_item}>建物面積:{data.building_area}㎡</p>
            <p className={styles.detail_data_item}>土地面積:{data.land_area}㎡</p>
          </>
        )}
        {data.property_type === 'land' && (
          <>
            <p className={styles.detail_data_item}>
              状態:{LandCurrentStatus[data.current_status as keyof typeof LandCurrentStatus]}
            </p>
            <p className={styles.detail_data_item}>土地面積:{data.land_area}㎡</p>
          </>
        )}
      </div>
      {showCompanyName && (
        <div className={styles.company_link_container}>
          <Link href={`/companies/${data.company_id}`} className={styles.company_link}>
            {data.display_name}
          </Link>
        </div>
      )}
    </div>
  );
});

SaleCaseCard.displayName = 'SaleCaseCard';

export default SaleCaseCard;
